'use client'

import { Fragment, useCallback, useState } from 'react'
import { chipClass } from './AnalyticsSection'
import { MarketMonthlyChart } from './MarketMonthlyChart'
import { PayoutDetailDrawer } from './PayoutDetailDrawer'
import {
  displayedCommissionRubles,
  formatDayMonth,
  formatPeriod,
  formatShare,
  monthName,
  pluralOrders,
  SALES_PAYOUT_DIFFERENCE,
  type LedgerState,
  type MarketMoneyLedgerRow,
  type MarketMoneyMonthlyPoint,
} from '@/lib/market-money'

export const LEDGER_STATE_META: Record<LedgerState, { label: string; className: string }> = {
  received: { label: 'Получено', className: 'bg-received-wash text-received' },
  expected: { label: 'Ожидается', className: 'bg-expected-wash text-gold-deep' },
  week_open: { label: 'Неделя идёт', className: 'bg-bone text-ink-muted' },
}

type MonthState = LedgerState | 'partial'

const MONTH_STATE_META: Record<MonthState, { label: string; className: string }> = {
  ...LEDGER_STATE_META,
  partial: { label: 'Частично получено', className: 'bg-expected-wash text-gold-deep' },
}

const HIGH_COMMISSION_BASIS_POINTS = 1500

type LedgerFilter = 'all' | 'received' | 'pending'

const FILTERS: ReadonlyArray<readonly [LedgerFilter, string]> = [
  ['all', 'Все'],
  ['received', 'Получено'],
  ['pending', 'Ожидается'],
]

export interface LedgerMonth {
  month: string
  rows: readonly MarketMoneyLedgerRow[]
  orders: number
  salesKopecks: number
  payoutKopecks: number
  commissionKopecks: number | null
  commissionShareBasisPoints: number | null
  firstPayoutDate: string
  lastPayoutDate: string
  state: MonthState
}

function monthState(rows: readonly MarketMoneyLedgerRow[]): MonthState {
  if (rows.some((row) => row.state === 'week_open')) return 'week_open'
  const received = rows.filter((row) => row.state === 'received').length
  if (received === rows.length) return 'received'
  return received === 0 ? 'expected' : 'partial'
}

// A merged settlement row belongs to the month its delivery weeks end in.
export function groupLedgerByMonth(rows: readonly MarketMoneyLedgerRow[]): LedgerMonth[] {
  const byMonth = new Map<string, MarketMoneyLedgerRow[]>()
  for (const row of rows) {
    const month = row.deliveryTo.slice(0, 7)
    const bucket = byMonth.get(month)
    if (bucket) bucket.push(row)
    else byMonth.set(month, [row])
  }
  return [...byMonth.entries()]
    .sort(([left], [right]) => right.localeCompare(left))
    .map(([month, monthRows]) => {
      const salesKopecks = monthRows.reduce((sum, row) => sum + row.salesKopecks, 0)
      const payoutKopecks = monthRows.reduce((sum, row) => sum + row.payoutKopecks, 0)
      const closed = monthRows.every((row) => row.commissionKopecks !== null)
      const commissionKopecks = closed ? salesKopecks - payoutKopecks : null
      const payoutDates = monthRows.map((row) => row.payoutDate).sort()
      return {
        month,
        rows: monthRows,
        orders: monthRows.reduce((sum, row) => sum + row.orders, 0),
        salesKopecks,
        payoutKopecks,
        commissionKopecks,
        commissionShareBasisPoints: commissionKopecks === null || salesKopecks === 0 ? null : Math.round((commissionKopecks / salesKopecks) * 10_000),
        firstPayoutDate: payoutDates[0],
        lastPayoutDate: payoutDates[payoutDates.length - 1],
        state: monthState(monthRows),
      }
    })
}

export function ledgerChartPoints(months: readonly LedgerMonth[]): MarketMoneyMonthlyPoint[] {
  return [...months].reverse().map((month) => ({
    month: month.month,
    salesKopecks: month.salesKopecks,
    orders: month.orders,
    receivedKopecks: month.rows.filter((row) => row.state === 'received').reduce((sum, row) => sum + row.payoutKopecks, 0),
    expectedKopecks: month.rows.filter((row) => row.state !== 'received').reduce((sum, row) => sum + row.payoutKopecks, 0),
  }))
}

type LedgerView = 'table' | 'chart'

function StateChip({ state }: { state: MonthState }) {
  const meta = MONTH_STATE_META[state]
  return <span className={`whitespace-nowrap rounded-full px-2.5 py-[3px] text-[11px] font-semibold ${meta.className}`}>{meta.label}</span>
}

export function LedgerStateChip({ state }: { state: LedgerState }) {
  return <StateChip state={state} />
}

function formatLedgerRubles(kopecks: number): string {
  return (kopecks / 100).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function matchesFilter(month: LedgerMonth, filter: LedgerFilter): boolean {
  if (filter === 'all') return true
  return filter === 'received' ? month.state === 'received' : month.state !== 'received'
}

function CommissionCell({ salesKopecks, payoutKopecks, commissionKopecks, shareBasisPoints }: {
  salesKopecks: number
  payoutKopecks: number
  commissionKopecks: number | null
  shareBasisPoints: number | null
}) {
  const high = shareBasisPoints !== null && shareBasisPoints > HIGH_COMMISSION_BASIS_POINTS
  return (
    <span className="flex items-baseline gap-2.5">
      <span className="tab-nums text-st-unpaid">{commissionKopecks === null ? '—' : displayedCommissionRubles(salesKopecks, payoutKopecks).toLocaleString('ru-RU')}</span>
      {shareBasisPoints !== null && (
        <span
          data-testid="ledger-commission-share"
          data-commission-level={high ? 'high' : 'normal'}
          title={high ? 'Высокая комиссия' : undefined}
          className={`tab-nums text-[12px] ${high ? 'text-st-unpaid' : 'text-ink-muted'}`}
        >
          {formatShare(shareBasisPoints, 0)}
        </span>
      )}
    </span>
  )
}

const HEAD_CELL = 'py-2.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-faint'

export function PayoutLedger({ rows, highlightPayoutDate = null }: { rows: readonly MarketMoneyLedgerRow[]; highlightPayoutDate?: string | null }) {
  const [view, setView] = useState<LedgerView>('table')
  const [filter, setFilter] = useState<LedgerFilter>('all')
  const [openMonths, setOpenMonths] = useState<ReadonlySet<string>>(() => new Set())
  const [selected, setSelected] = useState<{ row: MarketMoneyLedgerRow; opener: HTMLElement | null } | null>(null)
  const closeDrawer = useCallback(() => setSelected(null), [])
  const months = groupLedgerByMonth(rows).filter((month) => matchesFilter(month, filter))

  const toggleMonth = (month: string) => setOpenMonths((current) => {
    const next = new Set(current)
    if (next.has(month)) next.delete(month)
    else next.add(month)
    return next
  })

  return (
    <article aria-label="Лента выплат" className="surface-card flex flex-col">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-rule px-4 py-4 sm:px-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="eyebrow">Лента выплат</div>
          <div role="group" aria-label="Вид ленты" className="flex overflow-hidden rounded-[4px] border border-rule-strong text-[12px]">
            {([['table', 'Таблица'], ['chart', 'График']] as const).map(([value, label]) => (
              <button key={value} type="button" aria-pressed={view === value} onClick={() => setView(value)} className={`px-3 py-1.5 transition-colors ${view === value ? 'bg-ink text-ivory' : 'bg-paper text-ink-muted hover:text-ink'}`}>{label}</button>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          {FILTERS.map(([value, label]) => (
            <button key={value} type="button" aria-pressed={filter === value} onClick={() => setFilter(value)} className={chipClass(filter === value)}>{label}</button>
          ))}
        </div>
      </div>
      {view === 'chart' ? (
        <MarketMonthlyChart
          points={ledgerChartPoints(months)}
          salesLabel="Сумма покупок"
          footnote="Сумма покупок и выплаты — по месяцу доставки"
          expectedDatesFor={(key) => {
            const dates = months.find((month) => month.month === key)?.rows.filter((row) => row.state !== 'received').map((row) => formatDayMonth(row.payoutDate)).reverse() ?? []
            return dates.length ? dates.join(', ') : null
          }}
        />
      ) : (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1080px] table-fixed text-[14px]">
          <colgroup>
            <col className="w-[190px]" />
            <col className="w-[220px]" />
            <col className="w-[130px]" />
            <col className="w-[160px]" />
            <col className="w-[160px]" />
            <col className="w-[220px]" />
            <col />
          </colgroup>
          <thead className="border-b border-rule bg-ivory text-left">
            <tr>
              <th scope="col" className={`${HEAD_CELL} pl-6`}>Месяц доставок</th>
              <th scope="col" className={HEAD_CELL}>Выплаты</th>
              <th scope="col" className={HEAD_CELL}>Заказы</th>
              <th scope="col" className={`${HEAD_CELL} text-right`}>Сумма покупок, ₽</th>
              <th scope="col" className={`${HEAD_CELL} text-right`}>Выплата Маркета, ₽</th>
              <th scope="col" className={`${HEAD_CELL} pl-6`}>{SALES_PAYOUT_DIFFERENCE.label}, ₽</th>
              <th scope="col" className={`${HEAD_CELL} pr-6 text-right`}>Статус</th>
            </tr>
          </thead>
          <tbody>
            {months.length === 0 && (
              <tr><td colSpan={7} className="px-6 py-10 text-center text-[13px] text-ink-faint">Выплат пока нет</td></tr>
            )}
            {months.map((month) => {
              const open = openMonths.has(month.month)
              const label = `${monthName(month.month)} ${month.month.slice(0, 4)}`
              const monthHighlighted = month.rows.some((row) => row.payoutDate === highlightPayoutDate)
              return (
                <Fragment key={month.month}>
                  <tr
                    data-testid="payout-ledger-month"
                    data-highlighted={monthHighlighted}
                    onClick={() => toggleMonth(month.month)}
                    className={`cursor-pointer border-b border-rule transition-colors hover:bg-bone/40 ${monthHighlighted ? 'bg-row-highlight' : 'bg-paper'}`}
                  >
                    <td className="py-3.5 pl-6">
                      <button
                        type="button"
                        aria-expanded={open}
                        onClick={(event) => { event.stopPropagation(); toggleMonth(month.month) }}
                        className="flex items-center gap-2 font-semibold text-ink hover:text-gold-deep"
                      >
                        <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" className={`transition-transform ${open ? 'rotate-90' : ''}`}>
                          <path d="M3 1.5 6.5 5 3 8.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
                        </svg>
                        {label}
                      </button>
                    </td>
                    <td className="tab-nums py-3.5 text-[13px] text-ink-2">{formatPeriod(month.firstPayoutDate, month.lastPayoutDate)}</td>
                    <td className="tab-nums py-3.5 text-[13px] text-ink-muted">{pluralOrders(month.orders)}</td>
                    <td className="tab-nums py-3.5 text-right">{formatLedgerRubles(month.salesKopecks)}</td>
                    <td className="tab-nums py-3.5 text-right font-semibold">{formatLedgerRubles(month.payoutKopecks)}</td>
                    <td className="py-3.5 pl-6">
                      <CommissionCell salesKopecks={month.salesKopecks} payoutKopecks={month.payoutKopecks} commissionKopecks={month.commissionKopecks} shareBasisPoints={month.commissionShareBasisPoints} />
                    </td>
                    <td className="py-3.5 pr-6 text-right"><StateChip state={month.state} /></td>
                  </tr>
                  {open && month.rows.map((row) => {
                    const highlighted = row.payoutDate === highlightPayoutDate
                    return (
                      <tr
                        key={`${row.payoutDate}-${row.deliveryFrom}-${row.state}`}
                        data-testid="payout-ledger-row"
                        data-highlighted={highlighted}
                        onClick={(event) => setSelected({ row, opener: event.currentTarget.querySelector('button') })}
                        className={`cursor-pointer border-b border-rule text-[13px] transition-colors hover:bg-bone/40 ${highlighted ? 'bg-row-highlight' : 'bg-ivory'}`}
                      >
                        <td className="py-2.5 pl-12">
                          <button
                            type="button"
                            aria-label={`Открыть выплату ${formatDayMonth(row.payoutDate)}`}
                            onClick={(event) => { event.stopPropagation(); setSelected({ row, opener: event.currentTarget }) }}
                            className="tab-nums text-ink-2 hover:text-gold-deep"
                          >
                            {formatDayMonth(row.payoutDate)}
                          </button>
                        </td>
                        <td className="py-2.5">
                          <span className="flex items-center gap-2">
                            <span className="tab-nums w-24 text-ink-2">{formatPeriod(row.deliveryFrom, row.deliveryTo)}</span>
                            <span className="flex items-center gap-1 text-[11px] text-ink-faint"><span className="h-px w-6 bg-rule-strong" aria-hidden="true" />+{row.lagDays} дн</span>
                          </span>
                        </td>
                        <td className="tab-nums py-2.5 text-ink-muted">{pluralOrders(row.orders)}</td>
                        <td className="tab-nums py-2.5 text-right text-ink-2">{formatLedgerRubles(row.salesKopecks)}</td>
                        <td className="tab-nums py-2.5 text-right text-ink-2">{formatLedgerRubles(row.payoutKopecks)}</td>
                        <td className="py-2.5 pl-6">
                          <CommissionCell salesKopecks={row.salesKopecks} payoutKopecks={row.payoutKopecks} commissionKopecks={row.commissionKopecks} shareBasisPoints={row.commissionShareBasisPoints} />
                        </td>
                        <td className="py-2.5 pr-6 text-right"><LedgerStateChip state={row.state} /></td>
                      </tr>
                    )
                  })}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
      )}
      {view === 'table' && months.length > 0 && (
        <div className="px-4 py-3 text-[12px] text-ink-muted sm:px-6">
          Нажмите на месяц, чтобы увидеть выплаты по неделям доставок
        </div>
      )}
      {selected && <PayoutDetailDrawer row={selected.row} returnFocusTo={selected.opener} onClose={closeDrawer} />}
    </article>
  )
}
